import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../../slices/authSlice';
import isoLanguages from '../../isoLanguages';
import GitHubImportDialog from '../../GitHubImportDialog';
import '../../DashboardHome/settings.css';
import '../../DashboardHome/new-project.css';

const FOLDERS = ['Marketing', 'Brand', 'Product', 'Support', 'Legal', 'Operations'];

const WORKFLOWS = [
    { value: 'express', label: 'Express', desc: 'Machine translation + a single light review pass. Fastest turnaround.' },
    { value: 'standard', label: 'Standard', desc: 'AI draft → human translate → review → approve. Recommended.' },
    { value: 'premium', label: 'Premium', desc: 'Two-pass human translation with independent QA sign-off.' },
];

const CONNECTORS = [
    { name: 'Google Drive', cls: 'gd', letter: 'G' },
    { name: 'Figma', cls: 'fg', letter: 'F' },
    { name: 'GitHub', cls: 'gh', letter: 'G' },
    { name: 'Dropbox', cls: 'db', letter: 'D' },
];

const DEFAULT_TARGETS = [
    { code: 'DE', name: 'German' },
    { code: 'FR', name: 'French' },
    { code: 'JA', name: 'Japanese' },
    { code: 'ES', name: 'Spanish' },
    { code: 'PT-BR', name: 'Portuguese (BR)' },
];

const PRIORITIES = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
];

const ICN = {
    plus: (
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    x: (
        <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    check: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    copy: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <rect x="3" y="3" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 3V2a1 1 0 011-1h5a1 1 0 011 1v8a1 1 0 01-1 1h-1" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
};

const UPLOAD_ICON = (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
        <path d="M12 16V5m0 0L8 9m4-4l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5 15v3a1 1 0 001 1h12a1 1 0 001-1v-3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const estWords = (bytes) => Math.max(140, Math.round((bytes || 9000) / 6.2));

const extToFic = (name) => {
    const ext = ((name || '').split('.').pop() || '').toLowerCase();
    const map = {
        doc: ['DOC', ''], docx: ['DOC', ''], pdf: ['PDF', ''], txt: ['TXT', ''],
        xliff: ['XLI', 'xli'], xlf: ['XLI', 'xli'], xlsx: ['XLS', 'xli'], csv: ['CSV', 'xli'],
        json: ['JSO', 'jso'], yml: ['YML', 'jso'], yaml: ['YML', 'jso'],
        md: ['MD', 'md'], html: ['HTM', 'md'], htm: ['HTM', 'md'], idml: ['IDM', 'idm'],
    };
    return map[ext] || [ext.slice(0, 3).toUpperCase() || 'TXT', ''];
};

const peoplePalette = (idx) => `av-${(idx % 6) + 1}`;
const initialsOf = (m) => {
    const n = `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email || '?';
    return n.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
};

// ============ Stepper rail ============
const StepRail = ({ steps, step, setStep }) => (
    <nav className="set-rail np-rail">
        <div className="set-rail-group">Steps</div>
        {steps.map((s, i) => (
            <button
                key={s.id}
                type="button"
                className={`np-step ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`}
                onClick={() => { if (i <= step) setStep(i); }}
                disabled={i > step}
            >
                <span className="np-step-n">{i < step ? ICN.check : i + 1}</span>
                <span className="np-step-l">{s.label}</span>
            </button>
        ))}
        <div className="np-rail-foot">
            <span className="np-prog-dots">
                {steps.map((s, i) => <span key={s.id} className={i <= step ? 'on' : ''} />)}
            </span>
            Step {step + 1} of {steps.length}
        </div>
    </nav>
);

// ============ People picker ============
const PeopleField = ({ mode = 'multi', value, onChange, exclude = [], people }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);

    const selectedIds = mode === 'single' ? (value ? [value] : []) : (value || []);
    const byId = (id) => people.find((p) => p.id === id);
    const available = people.filter(
        (p) => !exclude.includes(p.id) && !(mode === 'multi' && selectedIds.includes(p.id)),
    );

    const pick = (id) => {
        if (mode === 'single') { onChange(id); setOpen(false); }
        else { onChange([...(value || []), id]); }
    };
    const remove = (id) => {
        if (mode === 'single') onChange('');
        else onChange((value || []).filter((x) => x !== id));
    };

    return (
        <div className="np-people">
            {selectedIds.map((id) => {
                const p = byId(id);
                if (!p) return null;
                return (
                    <span key={id} className="np-person-chip">
                        <span className={`av-s ${p.av}`}>{p.initials}</span>
                        <span className="nm">{p.name}{p.you ? ' (You)' : ''}</span>
                        <button type="button" className="x" aria-label={`Remove ${p.name}`} onClick={() => remove(id)}>{ICN.x}</button>
                    </span>
                );
            })}
            {(mode === 'multi' || selectedIds.length === 0) && (
                <div style={{ position: 'relative' }} ref={ref}>
                    <button type="button" className="np-person-add" onClick={() => setOpen((o) => !o)}>
                        {ICN.plus}{mode === 'single' ? 'Choose owner' : 'Add people'}
                    </button>
                    {open && (
                        <div className="np-person-pop">
                            {available.length === 0 && <div className="empty">Everyone added</div>}
                            {available.map((p) => (
                                <div key={p.id} className="opt" onClick={() => pick(p.id)} role="button" tabIndex={0}>
                                    <span className={`av-s ${p.av}`}>{p.initials}</span>
                                    <div className="meta">
                                        <b>{p.name}{p.you ? ' (You)' : ''}</b>
                                        <span>{p.role}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============ Settings primitives (matching design's SetBlock/SetField) ============
const SetBlock = ({ title, desc, flush, children }) => (
    <div className="set-block">
        <div className={`set-block-head ${flush ? 'flush' : ''}`}>
            <h3>{title}</h3>
            {desc && <p>{desc}</p>}
        </div>
        {children}
    </div>
);

const SetField = ({ label, desc, stack, top, children }) => (
    <div className={`set-field ${stack ? 'stack' : ''} ${top ? 'top' : ''}`}>
        <div className="set-field-label">
            <div className="lbl">{label}</div>
            {desc && <div className="desc">{desc}</div>}
        </div>
        <div className="set-field-control">{children}</div>
    </div>
);

const SetText = ({ value, onChange, placeholder }) => (
    <input
        className="set-input"
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
    />
);

const SetSelect = ({ value, onChange, options, minWidth }) => (
    <select
        className="set-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={minWidth ? { minWidth } : undefined}
    >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

const SetSegmented = ({ value, onChange, options }) => (
    <div className="bv-seg">
        {options.map((o) => (
            <button
                type="button"
                key={o.value}
                className={value === o.value ? 'on' : ''}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const SetToggle = ({ checked, onChange }) => (
    <button
        type="button"
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        style={{
            width: 38, height: 22, borderRadius: 999,
            border: 0,
            background: checked ? 'var(--accent)' : 'var(--bg-soft)',
            position: 'relative', cursor: 'pointer',
        }}
    >
        <span style={{
            position: 'absolute', top: 2,
            left: checked ? 18 : 2,
            width: 18, height: 18,
            background: 'white', borderRadius: 999,
            transition: 'left 0.15s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
    </button>
);

const SetRadioCards = ({ value, onChange, options }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
        {options.map((o) => (
            <button
                type="button"
                key={o.value}
                onClick={() => onChange(o.value)}
                style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    border: `1px solid ${value === o.value ? 'var(--accent)' : 'var(--line)'}`,
                    borderRadius: 10,
                    background: value === o.value ? 'var(--accent-soft)' : 'var(--bg)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: 'inherit',
                    display: 'flex', flexDirection: 'column', gap: 3,
                }}
            >
                <b style={{ fontSize: 13.5, color: 'var(--ink)' }}>{o.label}</b>
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{o.desc}</span>
            </button>
        ))}
    </div>
);

const SetLangChips = ({ langs, all, onAdd, onRemove }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);
    const codes = new Set(langs.map((l) => l.code));
    const available = all.filter((l) => !codes.has(l.code));
    return (
        <div className="np-people" ref={ref} style={{ position: 'relative' }}>
            {langs.map((l) => (
                <span key={l.code} className="np-person-chip">
                    <span className="av-s av-1" style={{ fontSize: 9 }}>{l.code.slice(0, 2)}</span>
                    <span className="nm">{l.name}</span>
                    <button type="button" className="x" onClick={() => onRemove(l.code)} aria-label={`Remove ${l.name}`}>{ICN.x}</button>
                </span>
            ))}
            <button type="button" className="np-person-add" onClick={() => setOpen((o) => !o)}>
                {ICN.plus}Add language
            </button>
            {open && (
                <div className="np-person-pop" style={{ maxHeight: 280, overflow: 'auto' }}>
                    {available.length === 0 && <div className="empty">All languages added</div>}
                    {available.slice(0, 50).map((l) => (
                        <div key={l.code} className="opt" role="button" tabIndex={0} onClick={() => { onAdd(l); setOpen(false); }}>
                            <span className="av-s av-1" style={{ fontSize: 9 }}>{l.code.slice(0, 2)}</span>
                            <div className="meta"><b>{l.name}</b><span>{l.code}</span></div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ============ STEP 1 — Details ============
const StepDetails = ({ d, up }) => {
    const [tagDraft, setTagDraft] = useState('');
    const addTag = () => {
        const v = tagDraft.trim();
        if (v && !d.tags.includes(v)) up('tags', [...d.tags, v]);
        setTagDraft('');
    };
    return (
        <>
            <div className="set-section-head">
                <h2>Project details</h2>
                <p>Name the project and file it under a workspace folder. You can change any of this later.</p>
            </div>
            <SetBlock title="Basics">
                <SetField label="Project name" desc="Shown across the dashboard, exports, and review links.">
                    <SetText value={d.name} onChange={(v) => up('name', v)} placeholder="e.g. Q3 Product Launch" />
                </SetField>
                <SetField label="Folder" desc="Group this project with related work.">
                    <SetSelect
                        value={d.folder}
                        onChange={(v) => up('folder', v)}
                        minWidth={180}
                        options={FOLDERS.map((f) => ({ value: f, label: f }))}
                    />
                </SetField>
                <SetField label="Description" desc="A short summary of what's being localized." top>
                    <textarea
                        className="set-input wide"
                        style={{ width: 320, minHeight: 70 }}
                        value={d.description}
                        placeholder="Optional — what is this content for?"
                        onChange={(e) => up('description', e.target.value)}
                    />
                </SetField>
                <SetField label="Priority" desc="Drives queue ordering and deadline reminders.">
                    <SetSegmented value={d.priority} onChange={(v) => up('priority', v)} options={PRIORITIES} />
                </SetField>
                <SetField label="Tags" desc="Press Enter to add. Used for filtering and reporting." stack top>
                    <div className="np-tags">
                        {d.tags.map((t) => (
                            <span key={t} className="np-tag">
                                {t}
                                <button type="button" className="x" onClick={() => up('tags', d.tags.filter((x) => x !== t))}>{ICN.x}</button>
                            </span>
                        ))}
                        <input
                            className="np-tag-input"
                            value={tagDraft}
                            placeholder="Add tag…"
                            onChange={(e) => setTagDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                        />
                    </div>
                </SetField>
            </SetBlock>
        </>
    );
};

// ============ STEP 2 — Languages ============
const StepLanguages = ({ d, up, sourceLangs, langCatalog }) => (
    <>
        <div className="set-section-head">
            <h2>Languages</h2>
            <p>Pick the source language and the targets to localize into. Defaults are inherited from your workspace.</p>
        </div>
        <SetBlock title="Source">
            <SetField label="Source language" desc="The language the content is authored in.">
                <SetSelect value={d.sourceLang} onChange={(v) => up('sourceLang', v)} options={sourceLangs} minWidth={220} />
            </SetField>
        </SetBlock>
        <SetBlock
            title="Targets"
            desc={`${d.targets.length} target ${d.targets.length === 1 ? 'language' : 'languages'} selected. Each becomes its own workstream.`}
        >
            <div className="set-field stack top">
                <div className="set-field-control" style={{ width: '100%' }}>
                    <SetLangChips
                        langs={d.targets}
                        all={langCatalog}
                        onAdd={(l) => up('targets', [...d.targets, l])}
                        onRemove={(code) => up('targets', d.targets.filter((t) => t.code !== code))}
                    />
                </div>
            </div>
            <SetField label="Add workspace defaults" desc="Quickly add the 5 languages configured in Settings.">
                <button
                    type="button"
                    className="set-btn sm"
                    onClick={() => {
                        const merged = [...d.targets];
                        DEFAULT_TARGETS.forEach((x) => {
                            if (!merged.find((t) => t.code === x.code)) merged.push(x);
                        });
                        up('targets', merged);
                    }}
                >
                    {ICN.plus} Add defaults
                </button>
            </SetField>
        </SetBlock>
    </>
);

// ============ STEP 3 — Content ============
//
// `openGithub` is provided by the parent (NewProject) — it flips the global
// GitHubImportDialog open. The dialog handles the OAuth status check, repo
// browsing, file selection and creates the Project itself on file click via
// /api/integrations/github/import. On success the parent navigates straight
// to the new Project. Other connectors keep their original placeholder
// behaviour (just adds a stub file row) for now.
const StepContent = ({ d, up, openGithub }) => {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);
    const [paste, setPaste] = useState(false);
    const [pasteText, setPasteText] = useState('');

    const addFiles = (fileList) => {
        const adds = Array.from(fileList).map((f, i) => {
            const [lbl, ic] = extToFic(f.name);
            return {
                id: `f${Date.now()}${i}`,
                name: f.name,
                src: 'Upload',
                words: estWords(f.size),
                lbl,
                ic,
                file: f,
            };
        });
        up('files', (files) => [...files, ...adds]);
    };
    const addConnector = (c) => {
        // GitHub gets the real flow — open the file browser dialog. The dialog
        // owns the OAuth status check, repo/file navigation and the
        // /github/import call; the parent navigates to the new Project on success.
        if (c.name === 'GitHub' && typeof openGithub === 'function') {
            openGithub();
            return;
        }
        // Other connectors: fall back to the existing placeholder until each gets
        // its own dialog wired (Figma uses an API key, Drive/Dropbox each need
        // their own file picker).
        const item = {
            id: `c${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
            name: c.name,
            src: c.name,
            words: 1000,
            lbl: 'DOC',
            ic: '',
            connector: c.name,
        };
        up('files', (files) => [...files, item]);
    };
    const addPaste = () => {
        const words = pasteText.trim().split(/\s+/).filter(Boolean).length;
        if (!words) return;
        up('files', (files) => [...files, {
            id: `p${Date.now()}`,
            name: 'Pasted text',
            src: 'Pasted',
            words,
            lbl: 'TXT',
            ic: '',
            pasted: pasteText,
        }]);
        setPasteText('');
        setPaste(false);
    };
    const totalWords = d.files.reduce((a, f) => a + f.words, 0);

    return (
        <>
            <div className="set-section-head">
                <h2>Source content</h2>
                <p>Add the files to translate. Upload from your computer, connect a source, or paste text directly.</p>
            </div>

            <SetBlock title="Add files" flush>
                <div className="np-content-pad">
                    <div
                        className={`np-drop ${drag ? 'drag' : ''}`}
                        onClick={() => inputRef.current && inputRef.current.click()}
                        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                        onDragLeave={() => setDrag(false)}
                        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
                        role="button"
                        tabIndex={0}
                    >
                        <div className="np-drop-ic">{UPLOAD_ICON}</div>
                        <b>Drag &amp; drop files here</b>
                        <span>or <u>browse</u> — DOCX, XLIFF, JSON, MD, CSV, HTML up to 50&nbsp;MB</span>
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            hidden
                            onChange={(e) => addFiles(e.target.files)}
                        />
                    </div>

                    <div className="np-or"><span>or connect a source</span></div>
                    <div className="np-conn-row">
                        {CONNECTORS.map((c) => (
                            <button key={c.name} type="button" className="np-conn" onClick={() => addConnector(c)}>
                                <span className={`ic ${c.cls}`}>{c.letter}</span>{c.name}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`np-conn ${paste ? 'on' : ''}`}
                            onClick={() => setPaste((p) => !p)}
                        >
                            <span className="ic paste">{ICN.copy}</span>Paste text
                        </button>
                    </div>

                    {paste && (
                        <div className="np-paste">
                            <textarea
                                value={pasteText}
                                onChange={(e) => setPasteText(e.target.value)}
                                placeholder="Paste source text to translate…"
                            />
                            <div className="row">
                                <span className="ct">
                                    {pasteText.trim().split(/\s+/).filter(Boolean).length} words
                                </span>
                                <div className="grow" />
                                <button type="button" className="set-btn sm" onClick={() => { setPaste(false); setPasteText(''); }}>Cancel</button>
                                <button type="button" className="set-btn sm primary" onClick={addPaste}>Add text</button>
                            </div>
                        </div>
                    )}
                </div>
            </SetBlock>

            {d.files.length > 0 && (
                <SetBlock
                    title="Files added"
                    flush
                    desc={`${d.files.length} ${d.files.length === 1 ? 'file' : 'files'} · ~${totalWords.toLocaleString()} words · ~${Math.round(totalWords / 8).toLocaleString()} segments`}
                >
                    {d.files.map((f) => (
                        <div className="set-list-row" key={f.id}>
                            <span className={`fic ${f.ic}`}>{f.lbl}</span>
                            <div className="li-main">
                                <b>{f.name}</b>
                                <span>{f.src} · ~{f.words.toLocaleString()} words</span>
                            </div>
                            <div className="grow" />
                            <button
                                type="button"
                                className="set-btn sm danger"
                                onClick={() => up('files', d.files.filter((x) => x.id !== f.id))}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </SetBlock>
            )}
        </>
    );
};

// ============ STEP 4 — Workflow & team ============
const StepWorkflow = ({ d, up, people }) => (
    <>
        <div className="set-section-head">
            <h2>Workflow &amp; team</h2>
            <p>Choose how the content moves from source to delivered, and who's responsible.</p>
        </div>

        <SetBlock title="Workflow" desc="The stages every language passes through before it ships.">
            <div className="set-field stack top">
                <div className="set-field-control" style={{ width: '100%' }}>
                    <SetRadioCards value={d.workflow} onChange={(v) => up('workflow', v)} options={WORKFLOWS} />
                </div>
            </div>
            <SetField label="Pre-translate on import" desc="Seed empty segments with machine translation so linguists start from a draft.">
                <SetToggle checked={d.pretranslate} onChange={(v) => up('pretranslate', v)} />
            </SetField>
            <SetField label="Inherit QA checks" desc="Use the workspace quality-assurance rules from Settings.">
                <SetToggle checked={d.inheritQa} onChange={(v) => up('inheritQa', v)} />
            </SetField>
        </SetBlock>

        <SetBlock title="Team">
            <SetField label="Project owner" desc="Accountable for delivery. Receives all project notifications." stack top>
                <PeopleField mode="single" value={d.owner} onChange={(v) => up('owner', v)} people={people} />
            </SetField>
            <SetField label="Collaborators" desc="Translators and reviewers with access to this project." stack top>
                <PeopleField
                    mode="multi"
                    value={d.collaborators}
                    onChange={(v) => up('collaborators', v)}
                    exclude={d.owner ? [d.owner] : []}
                    people={people}
                />
            </SetField>
        </SetBlock>

        <SetBlock title="Schedule">
            <SetField label="Due date" desc="Target delivery date for all languages.">
                <input
                    type="date"
                    className="set-input"
                    style={{ minWidth: 180 }}
                    value={d.due}
                    onChange={(e) => up('due', e.target.value)}
                />
            </SetField>
            <SetField label="Hard deadline" desc="Block delivery slipping past the due date without sign-off.">
                <SetToggle checked={d.deadlineStrict} onChange={(v) => up('deadlineStrict', v)} />
            </SetField>
        </SetBlock>
    </>
);

// ============ STEP 5 — Review ============
const SummaryRow = ({ k, children }) => (
    <div className="np-sum-row">
        <span className="k">{k}</span>
        <span className="v">{children}</span>
    </div>
);

const StepReview = ({ d, people, sourceLangs }) => {
    const owner = people.find((p) => p.id === d.owner);
    const totalWords = d.files.reduce((a, f) => a + f.words, 0);
    const wf = WORKFLOWS.find((w) => w.value === d.workflow);
    const srcLabel = (sourceLangs.find((s) => s.value === d.sourceLang) || {}).label || d.sourceLang;
    return (
        <>
            <div className="set-section-head">
                <h2>Review &amp; create</h2>
                <p>Confirm the setup below. You can revisit any step from the list on the left.</p>
            </div>
            <SetBlock title="Summary" flush>
                <div className="np-summary">
                    <SummaryRow k="Project">
                        <b>{d.name || <span className="np-missing">Untitled project</span>}</b>
                        <span className="np-folder">{d.folder}</span>
                    </SummaryRow>
                    {d.description && <SummaryRow k="Description">{d.description}</SummaryRow>}
                    <SummaryRow k="Languages">
                        <span className="p-lang src">{(d.sourceLang || '').slice(0, 2).toUpperCase()} →</span>
                        {d.targets.map((t) => <span key={t.code} className="p-lang">{t.code}</span>)}
                        <span className="np-dim">· {srcLabel}</span>
                    </SummaryRow>
                    <SummaryRow k="Content">
                        {d.files.length > 0 ? (
                            <span>
                                {d.files.length} {d.files.length === 1 ? 'file' : 'files'} · ~{totalWords.toLocaleString()} words · ~{Math.round(totalWords / 8).toLocaleString()} segments
                            </span>
                        ) : (
                            <span className="np-missing">No files added yet</span>
                        )}
                    </SummaryRow>
                    <SummaryRow k="Workflow">
                        {wf?.label}
                        <span className="np-dim">· {d.pretranslate ? 'pre-translate on' : 'no pre-translate'}</span>
                    </SummaryRow>
                    <SummaryRow k="Owner">
                        {owner ? (
                            <span className="np-who">
                                <span className={`av-s ${owner.av}`}>{owner.initials}</span>
                                {owner.name}{owner.you ? ' (You)' : ''}
                            </span>
                        ) : (
                            <span className="np-missing">Unassigned</span>
                        )}
                    </SummaryRow>
                    <SummaryRow k="Collaborators">
                        {d.collaborators.length > 0 ? (
                            <span className="av-stack">
                                {d.collaborators.map((id) => {
                                    const p = people.find((x) => x.id === id);
                                    return p ? <span key={id} className={`av-s ${p.av}`}>{p.initials}</span> : null;
                                })}
                            </span>
                        ) : (
                            <span className="np-dim">None</span>
                        )}
                    </SummaryRow>
                    <SummaryRow k="Due">
                        {d.due ? <b>{d.due}</b> : <span className="np-dim">No date set</span>}
                        {d.priority !== 'normal' && <span className={`np-prio ${d.priority}`}>{d.priority}</span>}
                    </SummaryRow>
                    {d.tags.length > 0 && (
                        <SummaryRow k="Tags">
                            <span className="np-tags inline">
                                {d.tags.map((t) => <span key={t} className="np-tag static">{t}</span>)}
                            </span>
                        </SummaryRow>
                    )}
                </div>
            </SetBlock>
        </>
    );
};

// ============ Success ============
const Success = ({ d, projectId, reset, navigate }) => {
    const totalWords = d.files.reduce((a, f) => a + f.words, 0);
    return (
        <div className="np-success">
            <div className="np-success-ic">{ICN.check}</div>
            <h2>Project created</h2>
            <p>
                <b>{d.name || 'Untitled project'}</b> is set up across {d.targets.length} {d.targets.length === 1 ? 'language' : 'languages'}.
                {d.pretranslate && totalWords > 0 ? ' Pre-translation is running now — drafts will be ready shortly.' : ''}
            </p>
            <div className="np-success-actions">
                <button
                    type="button"
                    className="set-btn primary"
                    onClick={() => navigate(projectId ? `/dashboard/projects/${projectId}` : '/dashboard/projects')}
                >
                    Open project
                </button>
                <button type="button" className="set-btn" onClick={() => navigate('/dashboard/projects')}>
                    All projects
                </button>
                <button type="button" className="set-btn" onClick={reset}>Create another</button>
            </div>
        </div>
    );
};

// ============ Main page ============
const STEPS = [
    { id: 'details', label: 'Details' },
    { id: 'languages', label: 'Languages' },
    { id: 'content', label: 'Source content' },
    { id: 'workflow', label: 'Workflow & team' },
    { id: 'review', label: 'Review & create' },
];

const INITIAL_DRAFT = {
    name: '',
    folder: 'Marketing',
    description: '',
    priority: 'normal',
    tags: [],
    sourceLang: 'en',
    targets: [],
    files: [],
    workflow: 'standard',
    pretranslate: true,
    inheritQa: true,
    owner: '',
    collaborators: [],
    due: '',
    deadlineStrict: false,
};

const NewProject = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const token = useSelector((s) => s.auth.token);
    const me = useSelector((s) => s.auth.user) || {};

    const [team, setTeam] = useState([]);
    const [step, setStep] = useState(0);
    const [draft, setDraft] = useState(INITIAL_DRAFT);
    const [created, setCreated] = useState(false);
    const [createdId, setCreatedId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // GitHub import dialog — opened from the "Connect a source" row in StepContent.
    // The dialog creates the Project itself via /api/integrations/github/import,
    // so on success we jump straight to it and bypass the rest of this form.
    const [githubOpen, setGithubOpen] = useState(false);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/team', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setTeam(Array.isArray(r.data) ? r.data : []))
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                }
            });
    }, [token, dispatch, navigate]);

    const people = useMemo(() => {
        const meRecord = {
            id: me._id || 'me',
            name: `${me.firstName || ''} ${me.lastName || ''}`.trim() || me.email || 'You',
            initials: initialsOf(me),
            av: peoplePalette(0),
            role: me.occupation || 'You',
            you: true,
        };
        const rest = team.map((m, i) => {
            const u = m.userId || m;
            return {
                id: u._id || `t-${i}`,
                name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || `Member ${i + 1}`,
                initials: initialsOf(u),
                av: peoplePalette(i + 1),
                role: m.role || 'Member',
            };
        });
        return [meRecord, ...rest];
    }, [team, me._id, me.firstName, me.lastName, me.email, me.occupation]);

    // Default the owner to "you" when people load
    useEffect(() => {
        if (!draft.owner && people.length > 0) {
            setDraft((d) => ({ ...d, owner: people[0].id }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [people.length]);

    const sourceLangs = useMemo(
        () => isoLanguages.map((l) => ({ value: l.code, label: l.name })),
        [],
    );

    const langCatalog = useMemo(
        () => isoLanguages.map((l) => ({ code: l.code.toUpperCase(), name: l.name })),
        [],
    );

    const up = (k, v) => setDraft((prev) => ({
        ...prev,
        [k]: typeof v === 'function' ? v(prev[k]) : v,
    }));

    const canContinue = step === 0
        ? draft.name.trim().length > 0
        : step === 1
            ? draft.targets.length > 0
            : true;

    const goNext = () => { if (step < STEPS.length - 1) setStep((s) => Math.min(STEPS.length - 1, s + 1)); };
    const goBack = () => setStep((s) => Math.max(0, s - 1));
    const reset = () => { setDraft(INITIAL_DRAFT); setStep(0); setCreated(false); setCreatedId(null); };

    const submit = async () => {
        setSubmitting(true);
        setError(null);
        const form = new FormData();
        form.append('projectName', draft.name);
        form.append('sourceLanguage', draft.sourceLang);
        form.append('targetLanguages', JSON.stringify(draft.targets.map((t) => t.code.toLowerCase())));
        if (draft.due) form.append('deadline', draft.due);
        if (draft.tags.length) form.append('tags', JSON.stringify(draft.tags));
        if (draft.folder) form.append('category', draft.folder);
        if (draft.description) form.append('description', draft.description);
        if (draft.collaborators.length) form.append('assignedTo', JSON.stringify(draft.collaborators));
        // Just upload the first file (legacy backend accepts one file per upload).
        const firstFile = draft.files.find((f) => f.file);
        if (firstFile) form.append('file', firstFile.file);
        try {
            const res = await axios.post('/api/new-translation/upload', form, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            });
            const proj = res.data && (res.data.project || (res.data.projects && res.data.projects[0]));
            const id = proj && (proj._id || proj.id);
            setCreatedId(id || null);
            setCreated(true);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                dispatch(logout());
                navigate('/login');
            } else {
                setError((err.response && err.response.data && err.response.data.msg) || 'Failed to create project.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">New project</h1>
                <div className="rs-page-actions">
                    <button
                        type="button"
                        className="btn-q"
                        onClick={() => navigate('/dashboard/projects')}
                    >
                        Cancel
                    </button>
                </div>
            </div>
            <p className="rs-page-sub">Set up a localization project in a few steps. Defaults are pulled from your workspace.</p>

            {created ? (
                <Success d={draft} projectId={createdId} reset={reset} navigate={navigate} />
            ) : (
                <div className="set-layout">
                    <StepRail steps={STEPS} step={step} setStep={setStep} />

                    <div className="set-body">
                        {step === 0 && <StepDetails d={draft} up={up} />}
                        {step === 1 && <StepLanguages d={draft} up={up} sourceLangs={sourceLangs} langCatalog={langCatalog} />}
                        {step === 2 && <StepContent d={draft} up={up} openGithub={() => setGithubOpen(true)} />}
                        {step === 3 && <StepWorkflow d={draft} up={up} people={people} />}
                        {step === 4 && <StepReview d={draft} people={people} sourceLangs={sourceLangs} />}

                        {error && (
                            <div style={{
                                marginTop: 12, color: '#b53e3e',
                                background: '#fbe1e1', padding: 10, borderRadius: 8, fontSize: 13,
                            }}>
                                {error}
                            </div>
                        )}

                        <div className="np-nav">
                            {step > 0 && (
                                <button type="button" className="set-btn" onClick={goBack}>← Back</button>
                            )}
                            <div className="grow" />
                            {step < STEPS.length - 1 ? (
                                <button
                                    type="button"
                                    className="set-btn primary"
                                    onClick={goNext}
                                    disabled={!canContinue}
                                    style={!canContinue ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                >
                                    Continue →
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="set-btn accent"
                                    onClick={submit}
                                    disabled={!draft.name.trim() || submitting}
                                    style={(!draft.name.trim() || submitting) ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                                >
                                    {ICN.check} {submitting ? 'Creating…' : 'Create project'}
                                </button>
                            )}
                        </div>

                        {step === 0 && !canContinue && (
                            <div className="np-hint">Give your project a name to continue.</div>
                        )}
                        {step === 1 && !canContinue && (
                            <div className="np-hint">Add at least one target language to continue.</div>
                        )}
                    </div>
                </div>
            )}

            {/*
              * GitHub source picker — opens when the user clicks the GitHub button
              * in the "Connect a source" row. The dialog handles every step:
              * if not connected, the underlying repos fetch returns
              * GITHUB_NOT_CONNECTED and the dialog surfaces a clear error.
              * On successful import it calls onImportSuccess with the new
              * Project id; we close the dialog and navigate to the Project.
              */}
            {/*
              * Build the `extra` payload from the form so /github/import can apply
              * everything the user has typed in this wizard onto the new Project(s).
              * Only include values the user actually set — undefined keys would
              * otherwise overwrite the backend's sensible defaults.
              */}
            <GitHubImportDialog
                open={githubOpen}
                onClose={() => setGithubOpen(false)}
                token={token}
                extra={{
                    ...(draft.name && draft.name.trim() ? { projectName: draft.name.trim() } : {}),
                    ...(draft.due ? { deadline: draft.due } : {}),
                    ...(draft.tags && draft.tags.length ? { tags: draft.tags } : {}),
                    ...(draft.folder ? { category: draft.folder } : {}),
                    ...(draft.description ? { description: draft.description } : {}),
                    ...(draft.collaborators && draft.collaborators.length ? { assignedTo: draft.collaborators } : {}),
                    ...(draft.workflow ? { workflow: draft.workflow } : {}),
                    ...(draft.priority ? { priority: draft.priority } : {}),
                    ...(draft.sourceLang ? { sourceLanguage: draft.sourceLang } : {}),
                    // Multi-target → one Project per locale, all under one projectGroupId.
                    ...(draft.targets && draft.targets.length
                        ? { targets: draft.targets.map((t) => (t.code || '').toLowerCase()).filter(Boolean) }
                        : {}),
                }}
                onImportSuccess={(projectId, projectIds) => {
                    setGithubOpen(false);
                    // When multiple Projects were created (one per target language),
                    // jump to the first — they're linked by projectGroupId so the
                    // user can navigate to siblings from there.
                    const first = projectId || (projectIds && projectIds[0]);
                    if (first) navigate(`/dashboard/projects/${first}`);
                }}
            />
        </>
    );
};

export default NewProject;
